import { AbortedError, ProhibitActionError } from 'src/common/api/extended-emerald-error';
import { restorePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { showEmeraldDialog } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { clearEmeraldModals } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { PriceModifierAction } from 'src/common/enumerations/price-modifier-action';
import { ItemCategories } from 'src/common/enumerations/rti-enums/item-categories';
import { getValueAsNumber } from 'src/common/helpers/number-helper';
import { IItemLookup } from 'src/common/interfaces/dtos/IItemLookup';
import { IMessageModal, MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { ActivityType } from 'src/operation-notify/enum/activities.enum';
import { initiateTendering } from 'src/tendering/business-logic/initiate-tendering';
import { exitTenderingMode } from 'src/tendering/business-logic/tendering-actions';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { EmeraldError, EmeraldErrorModal } from '../../common/api/emerald-error';
import { PriceOverrideType } from '../../common/enumerations/price-override-type';
import {
  ModifiedPriceIsEqualToOriginalPrice,
  NewPriceHigherThanOriginal,
  TenderAmountExceededEligibilityAmount,
} from '../../common/helpers/error-code';
import XLRLogger from '../../common/helpers/logger';
import { IElectronicJournalRecord } from '../../common/interfaces/states/IElectronicJournalRecord';
import { store } from '../../common/redux/configure-store';
import { LineItemAddDto } from '../../items-list/business-logic/service/interfaces/line-item-add.dto';
import { popupMessage } from '../../messages/business-logic/popup-message';
import { operationNotify } from '../../operation-notify/operation-notify';
import {
  IPriceOverridePayload,
  IPriceOverrideProps,
  showPriceOverrideDialog,
} from '../components/price-override/price-override';
import { handlePriceOverrideTareWeightItems } from './handle-tare-weight-items';
import { overrideItemPrice } from './override-item';

const source = 'business-logic/handle-price-override.ts';

export const initialExtendedPriceOverride = (): void => {
  initialPriceOverride(PriceOverrideType.Extended);
};

export const initialPriceOverride = (
  priceOverrideType: PriceOverrideType = PriceOverrideType.Regular,
): void => {
  const selectedItem = store.getState().itemList.ejItems.find(item => item.Selected);
  if (selectedItem) {
    priceOverrideWithOperation(selectedItem, priceOverrideType).catch(popupMessage);
  }
};

export const priceOverrideWithOperation = async (
  selectedItem: IElectronicJournalRecord,
  priceOverrideType: PriceOverrideType,
  lineItem?: LineItemAddDto,
  itemLookup?: IItemLookup,
): Promise<void> => {
  return operationNotify(ActivityType.PriceModify).then(() =>
    handlePriceOverride(selectedItem, priceOverrideType, lineItem, itemLookup),
  );
};

export const showTenderAmountEligibilityModal = (e: EmeraldError): void => {
  const restrictedLine = getValueAsNumber(e.data?.restrictedLine ?? '');
  const restrictedTender =
    store
      .getState()
      .itemList.ejItems.filter(
        item => item.Type === 'Tender' && item.SequenceNumber === restrictedLine,
      )[0]?.Name ?? '';
  const message: IMessageModal = {
    message: 'Tender_Restriction',
    subMessage: 'TenderAmountExceededEligibilityAmountVoucher',
    subMessageArgs: { arg: restrictedTender },
    type: EmeraldDialogTypes.Info,
    size: MessageModalSizeEnum.Medium,
    onAccept: clearEmeraldModals,
    acceptButtonLabel: 'Ok',
    hideCancelButton: true,
    disableBackdropClick: true,
  };
  showEmeraldDialog(message);
};

export const showTenderAmountEligibilityModalCustomerSearchModal = (): void => {
  const message: IMessageModal = {
    message: 'Tender_Restriction',
    subMessage: 'TenderAmountExceededEligibilityAmountCustomerSearch',
    type: EmeraldDialogTypes.Info,
    size: MessageModalSizeEnum.Medium,
    onAccept: clearEmeraldModals,
    acceptButtonLabel: 'Ok',
    hideCancelButton: true,
    disableBackdropClick: true,
  };
  showEmeraldDialog(message);
};

export const handlePriceOverride = async (
  selectedItem: IElectronicJournalRecord,
  priceOverrideType: PriceOverrideType,
  lineItem?: LineItemAddDto,
  itemLookup?: IItemLookup,
): Promise<void> => {
  const isTendering = store.getState().tendering.isTendering;
  if (isTendering) {
    await withSpinner(async () => {
      await exitTenderingMode();
    });
  }
  return new Promise<void>((resolve, reject) => {
    const onSubmit = (payload: IPriceOverridePayload): void => {
      const numericUnitPrice = Number(selectedItem.NumericUnitPrice?.toFixed(2));
      const xlrConfig = getCurrentXLRConfig();
      const runClientValidationWhenOverridingToHigherPrice =
        xlrConfig.runClientValidationWhenOverridingToHigherPrice;
      const openPriceItem = selectedItem.PriceModifier === 'ResetPrice';
      const priceVerifyItem = selectedItem.PriceModifier === 'PriceVerify';

      if (isEqualTo(payload.value, numericUnitPrice, payload.action)) {
        popupMessage(
          new EmeraldErrorModal({
            type: EmeraldDialogTypes.Info,
            message: ModifiedPriceIsEqualToOriginalPrice,
          }),
        );
        return;
      }
      if (
        /*
        Emerald does not have this validation on server, while Emerlad GX relies here on server response
        */
        runClientValidationWhenOverridingToHigherPrice &&
        isMoreThan(
          payload.value,
          selectedItem.RegularPrice?.unitPrice ?? numericUnitPrice,
          payload.action,
        ) &&
        !openPriceItem &&
        !priceVerifyItem
      ) {
        popupMessage(new EmeraldError(NewPriceHigherThanOriginal));
        return;
      }
      overrideItemPrice(payload, selectedItem, lineItem)
        .then((): void => {
          XLRLogger.debug({ message: 'Price Override Submitted: Success', source });
          if (isTendering) {
            initiateTendering();
          }
          resolve();
        })
        .catch(e => {
          if (e.code === TenderAmountExceededEligibilityAmount) {
            showTenderAmountEligibilityModal(e);
          } else {
            if (e instanceof ProhibitActionError) {
              e.showInfo = true;
            }
            popupMessage(e);
          }
        });
    };

    const onCancel = (): void => reject(new AbortedError());

    const showDialog = (): void => {
      const priceOverrideProps: IPriceOverrideProps = {
        selectedItem,
        onSubmit,
        onCancel,
        priceOverrideType,
      };

      showPriceOverrideDialog(priceOverrideProps);
      XLRLogger.debug({ message: 'Price Override View On', source });
    };

    if (
      lineItem &&
      itemLookup &&
      itemLookup?.linkedItemsGroup?.groupCategory === ItemCategories.Tare
    ) {
      handlePriceOverrideTareWeightItems(lineItem, itemLookup)
        .then(showDialog)
        .catch(onCancel);
    } else {
      showDialog();
    }
  }).finally(() => {
    restorePage();
    XLRLogger.debug({ message: 'Price Override View Closed', source });
  });
};

const isMoreThan = (value: string, originalPrice: number, action: PriceModifierAction): boolean => {
  const _value = getValueAsNumber(value);
  return action === PriceModifierAction.NewPrice ? _value > originalPrice : false;
};

const isEqualTo = (value: string, originalPrice: number, action: PriceModifierAction): boolean => {
  const _value = getValueAsNumber(value);
  return action === PriceModifierAction.NewPrice ? _value === originalPrice : _value === 0;
};
